/**
 * ModelSelector — dropdown to pick the model for the agent.
 *
 * Two routing paths:
 *  - **Claude** ids (``claude-*``) go directly to api.anthropic.com via
 *    claude-agent-sdk and keep prompt caching enabled.
 *  - **OpenRouter** ids (``openrouter/...``) route through the in-process
 *    LiteLLM proxy (cowork/ui/llm_router.py) using a single ``openrouter/*``
 *    wildcard — "one API, all models". The full catalog is fetched from
 *    ``/settings/openrouter-models`` (server-side cached) and is searchable
 *    here since it runs to hundreds of models.
 *
 * Direct OpenAI / Gemini integrations are intentionally hidden — Nbyula
 * reaches those models through OpenRouter instead.
 *
 * The first option is "Auto" — sentinel ``auto`` which tells the backend to
 * pick the highest-priority provider that is both configured and not on
 * cooldown (Claude → OpenRouter). On Auto the trigger reads just "Auto": the
 * resolved model is already stated in the system message and on every message
 * bubble, so repeating it here was noise. The resolution is still tracked
 * internally (``effectiveModel``) — it drives the cooldown-aware switch lock.
 *
 * Keep the Claude list in lockstep with:
 *  - `cowork/ui/model_limits.py::MODEL_CONTEXT_LIMITS`
 *  - `cowork/ui/llm_router.py::_CLAUDE_CATALOG`
 * OpenRouter models are dynamic — no mirror needed.
 */

import { useState, useRef, useEffect } from "react";
import { useChatStore, useConversationIsRunning } from "@/stores/chatStore";
import { apiFetch } from "@/api/client";
import { useModelCatalogStore } from "@/stores/modelCatalogStore";

type Provider = "claude" | "openrouter";

interface Model {
  readonly id: string;
  readonly provider: Provider;
  readonly label: string;
  readonly desc: string;
  /** 1 (cheapest) … 5 (flagship). Mirrors the backend ``cost_class`` so the
   *  five-dot cost meter and the premium-confirm threshold work the same
   *  for Claude rows as they do for the OpenRouter catalog. */
  readonly cost_class: number;
}

/**
 * Sentinel ID sent to the backend to mean "you pick — failover Claude →
 * OpenRouter based on which provider keys are configured and which aren't
 * currently on cooldown". Mirrored in
 * ``cowork/ui/llm_router.py::AUTO_MODEL_ID``.
 */
const AUTO_MODEL_ID = "auto";

interface ProviderStatus {
  provider: Provider;
  configured: boolean;
  cooled_down: boolean;
  cooldown_remaining_s: number | null;
  cooldown_reason: string | null;
  default_model: string;
}

// Claude lineup is static (Claude stays as-is). OpenRouter models are
// fetched at runtime.
// ``cost_class`` mirrors cowork/ui/llm_router.py::_CLAUDE_CATALOG
// (Haiku=fast=2, Sonnet=standard=3, every Opus + Fable=flagship=5).
// Listed in ascending capability, so the strongest options sit at the bottom
// nearest the trigger. Opus 5 and Opus 4.8 both run at "xhigh" thinking
// effort — the backend pins that from the model id (see
// llm_router::default_effort_for_model); nothing to pass here.
const CLAUDE_MODELS: readonly Model[] = [
  { id: "claude-haiku-4-5-20251001", provider: "claude", label: "Haiku 4.5",  desc: "Fastest & cheapest, 200K",          cost_class: 2 },
  { id: "claude-sonnet-4-6",         provider: "claude", label: "Sonnet 4.6", desc: "Fast & capable, 1M",                cost_class: 3 },
  { id: "claude-opus-4-7",           provider: "claude", label: "Opus 4.7",   desc: "Older flagship, 1M",                cost_class: 5 },
  { id: "claude-opus-4-8",           provider: "claude", label: "Opus 4.8",   desc: "Previous flagship, 1M",             cost_class: 5 },
  { id: "claude-opus-5",             provider: "claude", label: "Opus 5",     desc: "Default: most capable Opus · xhigh thinking, 1M", cost_class: 5 },
  { id: "claude-fable-5",            provider: "claude", label: "Fable 5",    desc: "Previous Mythos-class, 1M",         cost_class: 5 },
  { id: "claude-fable-5-1",          provider: "claude", label: "Fable 5.1",  desc: "Most intelligent Claude, 1M",       cost_class: 5 },
] as const;

const PROVIDER_LABEL: Record<Provider, string> = {
  claude: "Claude",
  openrouter: "OpenRouter",
};

// Color for the small dot next to each option. Jove for Claude, amber for
// OpenRouter (its brand accent) — an at-a-glance provider hint.
const PROVIDER_DOT: Record<Provider, string> = {
  claude: "bg-jove",
  openrouter: "bg-amber-500",
};

/**
 * Provider-status poll interval. The cooldown buckets in the backend are
 * 15s–60s for the temporary cases, so 30s gets the user a status pill
 * update within one bucket of the actual recovery on average.
 */
const PROVIDER_POLL_MS = 30_000;

/**
 * How long a ``provider_cooled_down`` WS event keeps the model-switch lock
 * lifted on its own, before the polled provider status takes over as the
 * authority. Just longer than one poll bucket so a confirming (or clearing)
 * poll always lands inside the window — this only bridges the gap until the
 * next poll, it doesn't decide recovery.
 */
const PROVIDER_BLOCK_LOCK_LIFT_MS = PROVIDER_POLL_MS + 5_000;

// How many OpenRouter rows to render at once. The catalog is hundreds of
// models; rendering them all bloats the DOM and the dropdown. We show this
// many (cheapest-first, or best search matches) and prompt the user to
// search to reach the rest — no silent truncation.
const OPENROUTER_RENDER_CAP = 50;

function lookupProvider(modelId: string | null | undefined): Provider | null {
  if (!modelId) return null;
  if (modelId.startsWith("claude-")) return "claude";
  if (modelId.startsWith("openrouter/")) return "openrouter";
  return null;
}

// ─── Cost-optimization guardrails ──────────────────────────────────────
//
// People with low AI literacy were burning OpenRouter quota by picking
// flagship models without realising the cost. These helpers make cost
// legible (a five-dot meter + plain tier word) and gate the two expensive
// moments: picking a premium model, and switching models on a long chat.

/** Pick of cost_class at or above which we interrupt with a confirmation.
 *  4–5 are the "smart"/"flagship" tiers — many times the per-token price
 *  of an everyday model. */
const PREMIUM_COST_CLASS = 4;

/**
 * Context size (tokens) at or above which the model picker hard-locks.
 *
 * Switching models resets the prompt cache (it's keyed per model), so the
 * next turn re-sends the *entire* conversation to the new model as fresh,
 * un-cached input — a sudden, surprising charge that scales with history.
 * Above this many cached tokens we stop the switch entirely and tell the
 * user to start a new chat instead. Tunable: too low nags on modest chats,
 * too high lets a large re-bill slip through. ~50K ≈ a few cents to dollars
 * of fresh input depending on the model — a sensible "considerable" line.
 */
const MODEL_SWITCH_LOCK_TOKENS = 50_000;

/** Plain-language tier word from a 1–5 cost_class — jargon-free for
 *  non-technical users ("flagship" means nothing to them; "Premium" does). */
function tierWord(costClass: number): string {
  if (costClass <= 2) return "Everyday";
  if (costClass === 3) return "Balanced";
  return "Premium";
}

/** Brand severity colour for a cost_class — reuses the UsageBadge palette
 *  (oxy = safe/cheap, gold = warn/mid, hydra = danger/expensive). */
function tierColor(costClass: number): string {
  if (costClass <= 2) return "text-oxy";
  if (costClass === 3) return "text-gold";
  return "text-hydra";
}

/** Five-dot cost meter: filled (coloured) up to ``cost_class``, hollow
 *  after — ●○○○○ cheapest → ●●●●● flagship. Purely visual; the tier word
 *  carries the same meaning in text for screen readers. */
function CostMeter({ costClass }: { costClass: number }) {
  const c = Math.max(1, Math.min(5, Math.round(costClass) || 3));
  const color = tierColor(c);
  return (
    <span
      className="ml-auto shrink-0 text-[9px] leading-none tracking-tight"
      title={`${tierWord(c)} cost tier`}
      aria-label={`${tierWord(c)} cost tier`}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= c ? color : "text-space-30"} aria-hidden>
          {i <= c ? "●" : "○"}
        </span>
      ))}
    </span>
  );
}

/** Human-readable input price ("$15/M input tokens") from OpenRouter's
 *  per-token ``pricing.prompt`` string. Returns null when unknown so the
 *  confirm panel falls back to tier wording rather than inventing a number. */
function inputPriceText(pricing?: { prompt?: string }): string | null {
  const raw = pricing?.prompt;
  if (raw == null) return null;
  const perMillion = parseFloat(raw) * 1e6;
  if (!Number.isFinite(perMillion) || perMillion <= 0) return null;
  const v = perMillion >= 1 ? perMillion.toFixed(0) : perMillion.toFixed(2);
  return `$${v}/M input tokens`;
}

/** What the premium-confirm panel needs — provider-agnostic so a Claude
 *  Opus row and an OpenRouter flagship use the same dialog. */
interface PendingPremium {
  id: string;
  label: string;
  costClass: number;
  priceText: string | null;
}

/** Props for {@link ModelSelector}. */
interface ModelSelectorProps {
  /**
   * Whether a running agent disables the picker. Defaults to ``true``.
   *
   * ``isAgentRunning`` is scoped to the **active** conversation, so the chat
   * footer (default) locks the picker only while the chat the user is *viewing*
   * has a turn in flight — switching models mid-turn there is meaningless.
   *
   * The home / new-chat composer passes ``false``: it composes a not-yet-created
   * conversation that has no in-flight turn of its own, so a background task
   * still running on the chat the user just left must never disable the picker
   * here — choosing a model is exactly what the user came to the new chat to do.
   * The switch lock (keyed off ``contextTokens``) is independent of this flag
   * and still applies wherever it would naturally fire.
   */
  lockWhileRunning?: boolean;
  /**
   * Whether a model pick is persisted to the active conversation. Defaults to
   * ``true`` (the chat footer, which is bound to the open chat).
   *
   * The home / new-chat composer passes ``false``: it is NOT bound to a
   * conversation (``activeConversationId`` still points at the previously
   * opened chat there), so persisting would overwrite the wrong chat's model.
   * Its pick is carried into the new conversation by ``createConversation``
   * instead. The in-memory selection still updates either way.
   */
  persistSelection?: boolean;
  /**
   * Render the selector as an inert, read-only label. Defaults to ``false``.
   *
   * Used by the ADR-0009 admin read-only view: a manager viewing a report's
   * chat sees *which* model the conversation ran on, but must not be able to
   * change it. When true the pill drops its dropdown, chevron, switch-lock
   * icon and hover affordance, and the provider-availability poll is skipped
   * (only the OpenRouter catalog is still fetched, to resolve a non-Claude
   * model's display label). The label text is identical to the owner's view —
   * it reads straight from the hydrated conversation. A conversation left on
   * Auto reads "Auto" here too; which model each turn actually ran on is on
   * the message bubbles, which is where per-turn resolution belongs anyway.
   */
  readOnly?: boolean;
}

export function ModelSelector({
  lockWhileRunning = true,
  persistSelection = true,
  readOnly = false,
}: ModelSelectorProps) {
  const selectedModel = useChatStore((s) => s.selectedModel);
  const setSelectedModel = useChatStore((s) => s.setSelectedModel);
  const effectiveModel = useChatStore((s) => s.effectiveModel);
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  // Durable row ∪ local task set — the same predicate the API enforces, so the
  // picker is never enabled for a switch the server is about to refuse (409).
  // Notably true for a turn running on the OTHER environment, which the local
  // flag alone does not see.
  const isAgentRunning = useConversationIsRunning(activeConversationId);
  // Size of the last turn's prompt — what gets re-billed as fresh input if
  // the model changes and the prompt cache resets. Drives the switch lock.
  const contextTokens = useChatStore((s) => s.contextTokens);
  // Providers blocked this session (rate-limit / quota / auth / overload),
  // captured the instant the ``provider_cooled_down`` WS event fires. Used to
  // lift the switch lock immediately so the user can escape a blocked provider.
  const cooledDownProviders = useChatStore((s) => s.cooledDownProviders);
  const [open, setOpen] = useState(false);
  const [providerStatus, setProviderStatus] = useState<ProviderStatus[]>([]);
  // Shared with ``getContextLimit`` (via modelCatalogStore) so the Context tab
  // sizes an OpenRouter model against its real window, not the 200K default.
  const orModels = useModelCatalogStore((s) => s.models);
  const loadCatalog = useModelCatalogStore((s) => s.load);
  const [search, setSearch] = useState("");
  // A premium pick awaiting confirmation (null = normal list). Set instead
  // of switching immediately when the user taps a cost_class ≥ 4 model.
  const [pendingPremium, setPendingPremium] = useState<PendingPremium | null>(null);
  // Max height for the desktop menu when it opens *upward* (``sm:bottom-full``),
  // measured so its top never crosses the top navbar. ``undefined`` on mobile,
  // where the menu is a fixed bottom sheet instead. See the measuring effect.
  const [menuMaxH, setMenuMaxH] = useState<number | undefined>(undefined);
  const ref = useRef<HTMLDivElement>(null);

  // Poll provider availability so the Auto entry can be annotated and
  // per-provider rows can show cooldown state. Skipped in read-only mode —
  // the inert pill shows no dropdown or cooldown annotations, so there's
  // nothing to poll for (the label resolves from the catalog alone).
  useEffect(() => {
    if (readOnly) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await apiFetch<{ providers: ProviderStatus[] }>(
          "/settings/available-providers",
        );
        if (!cancelled) setProviderStatus(res.providers ?? []);
      } catch {
        // Non-fatal — the dropdown still works without status annotations.
      }
    };
    load();
    const id = window.setInterval(load, PROVIDER_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Fetch the OpenRouter catalog once per page load (it's server-side cached,
  // so this is cheap and stable across opens); the store de-dupes repeat
  // calls. Failure leaves the list empty — Claude still works and the
  // OpenRouter group just shows a hint.
  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reset a half-finished premium confirmation whenever the menu closes, so
  // reopening always starts on the model list rather than a stale warning.
  useEffect(() => {
    if (!open) setPendingPremium(null);
  }, [open]);

  // Cap the upward-opening desktop menu to the gap between the trigger and the
  // top navbar. The menu is anchored ``sm:bottom-full`` (its bottom pinned to
  // the trigger top, growing up), so a fixed 28rem height overshoots into the
  // header when the trigger sits high on the page — as it does in the home
  // composer, which is vertically centred rather than at the screen bottom.
  // We measure the trigger's viewport offset and bound the height so the menu's
  // top stays a comfortable margin below the 56px (``h-14``) navbar. Re-measured
  // on resize. Mobile uses the fixed bottom-sheet layout, so its height is left
  // to CSS (``max-h-[70vh]``) and ``menuMaxH`` stays undefined there.
  useEffect(() => {
    if (!open) return;
    const NAV_CLEARANCE = 76; // 56px navbar + mb-1.5 offset + breathing room
    const DESIGN_MAX = 448; // matches sm:max-h-[28rem]
    const measure = () => {
      const desktop = window.matchMedia("(min-width: 640px)").matches;
      const top = ref.current?.getBoundingClientRect().top ?? 0;
      const available = Math.floor(top - NAV_CLEARANCE);
      setMenuMaxH(
        desktop && available > 0 ? Math.min(DESIGN_MAX, available) : undefined,
      );
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);

  const isAuto = selectedModel === AUTO_MODEL_ID;

  // Resolve a label for any model id (Claude static or OpenRouter dynamic).
  const labelFor = (modelId: string | null | undefined): string | null => {
    if (!modelId) return null;
    const claude = CLAUDE_MODELS.find((m) => m.id === modelId);
    if (claude) return claude.label;
    const or = orModels.find((m) => m.id === modelId);
    if (or) return or.label;
    // Fall back to the bare slug for an OpenRouter id whose catalog row
    // hasn't loaded yet, otherwise the raw id.
    if (modelId.startsWith("openrouter/")) return modelId.slice("openrouter/".length);
    return modelId;
  };

  // Derive the "currently shown" model for the chevron label.
  const effective = isAuto ? effectiveModel : selectedModel;
  const effectiveProvider = lookupProvider(effective);
  const effectiveLabel = labelFor(effective);
  const currentProvider = lookupProvider(effective);

  const configuredCount = providerStatus.filter((p) => p.configured).length;
  const autoHint =
    configuredCount === 0
      ? "No providers configured"
      : configuredCount === 1
        ? "Only one provider configured — no failover"
        : "Picks the best available provider";

  // Filter + cap the OpenRouter list. Search matches label or id so both
  // "opus" and "anthropic/claude" work. Already sorted cheapest-first by
  // the backend.
  const q = search.trim().toLowerCase();
  const orFiltered = q
    ? orModels.filter(
        (m) => m.label.toLowerCase().includes(q) || m.id.toLowerCase().includes(q),
      )
    : orModels;
  const orVisible = orFiltered.slice(0, OPENROUTER_RENDER_CAP);
  const orHidden = orFiltered.length - orVisible.length;

  const claudeStatus = providerStatus.find((p) => p.provider === "claude");
  const orStatus = providerStatus.find((p) => p.provider === "openrouter");

  // Show the Claude category only for installs that still have a Claude
  // OAuth token (the ``claude`` provider reports ``configured``). New
  // OpenRouter-only installs hide it entirely — they reach Claude models
  // through OpenRouter. Hidden until the provider poll resolves so a fresh
  // install never flashes a Claude section it can't use.
  const showClaude = !!claudeStatus?.configured;

  const annotationFor = (status?: ProviderStatus): string | null => {
    if (!status) return null;
    if (!status.configured) return "needs key";
    if (status.cooled_down) {
      return status.cooldown_remaining_s
        ? `cooled down (${Math.ceil(status.cooldown_remaining_s)}s)`
        : "cooled down";
    }
    return null;
  };

  // ── Switch lock ──
  // A large cached history makes switching models expensive: the new model
  // can't read the previous model's prompt cache, so the entire history
  // re-bills as fresh input on the very next turn. Above the threshold we
  // lock the picker entirely and steer the user to a new chat.
  //
  // The lock is a property of the *selected* conversation: ``contextTokens``
  // is reset to 0 whenever no conversation is active (on home navigation, see
  // ``chatStore.resetActiveContext``), so a new conversation always starts
  // fully switchable.
  //
  // EXCEPTION — provider block: if the model that's actually running belongs to
  // a provider that's currently cooled down (rate-limit / quota / auth /
  // overload), we LIFT the lock so the user can escape to a different provider.
  // The cache-protection rationale evaporates when the cached provider is
  // unusable anyway. Two signals, unioned so the lock lifts *immediately*: the
  // polled provider status (authoritative, self-clears on recovery — but up to
  // PROVIDER_POLL_MS stale) OR the ``provider_cooled_down`` WS event captured the
  // instant a block hits (``cooledDownProviders``, bounded by recency so it
  // decays rather than sticking forever). (``effectiveProvider`` is derived above
  // via ``lookupProvider(effective)``.)
  const polledCooled = effectiveProvider
    ? (providerStatus.find((p) => p.provider === effectiveProvider)?.cooled_down ?? false)
    : false;
  const recentlyCooled =
    !!effectiveProvider &&
    Date.now() - (cooledDownProviders[effectiveProvider]?.ts ?? 0) < PROVIDER_BLOCK_LOCK_LIFT_MS;
  const effectiveProviderCooled = polledCooled || recentlyCooled;
  const switchLocked =
    contextTokens >= MODEL_SWITCH_LOCK_TOKENS && !effectiveProviderCooled;
  const triggerDisabled = (lockWhileRunning && isAgentRunning) || switchLocked;
  const lockMessage = switchLocked
    ? `Model locked — this chat has ~${Math.round(contextTokens / 1000)}K tokens of cached ` +
      `history. Switching models now resets the prompt cache and re-bills all of it as fresh ` +
      `input on the new model. Start a new chat to use a different model.`
    : undefined;

  // Commit a model immediately: close the menu, clear any pending confirm.
  // ``persistSelection`` is false in the new-chat composer so the pick isn't
  // written to the previously opened chat (which is still ``activeConversationId``).
  const commit = (id: string) => {
    setSelectedModel(id, persistSelection);
    setPendingPremium(null);
    setSearch("");
    setOpen(false);
  };

  // Route a row click. Premium picks (cost_class ≥ 4) detour through a
  // confirmation panel; everything else switches straight away.
  const choose = (
    id: string,
    label: string,
    costClass: number,
    pricing?: { prompt?: string },
  ) => {
    if (costClass >= PREMIUM_COST_CLASS) {
      setPendingPremium({ id, label, costClass, priceText: inputPriceText(pricing) });
    } else {
      commit(id);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={readOnly ? undefined : () => !triggerDisabled && setOpen(!open)}
        disabled={readOnly ? false : triggerDisabled}
        title={readOnly ? "Model used for this conversation (read-only)" : lockMessage}
        aria-readonly={readOnly || undefined}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap
                   bg-space-20 text-space-50 transition-colors ${
                     readOnly
                       ? "cursor-default"
                       : "hover:bg-space-30 hover:text-space disabled:opacity-40 disabled:cursor-not-allowed"
                   }`}
      >
        {!readOnly && switchLocked && (
          <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 0h10.5a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5H6.75a1.5 1.5 0 01-1.5-1.5v-6a1.5 1.5 0 011.5-1.5z" />
          </svg>
        )}
        {isAuto ? (
          <>
            <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-jove to-amber-500" />
            <span>Auto</span>
          </>
        ) : (
          <>
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                currentProvider ? PROVIDER_DOT[currentProvider] : "bg-space-40"
              }`}
            />
            <span>
              {currentProvider
                ? `${PROVIDER_LABEL[currentProvider]} ${effectiveLabel}`
                : selectedModel}
            </span>
          </>
        )}
        {!readOnly && (
          <svg className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {!readOnly && open && (
        <div
          style={menuMaxH != null ? { maxHeight: `${menuMaxH}px` } : undefined}
          className="fixed inset-x-3 bottom-24 max-h-[70vh]
                        sm:absolute sm:inset-x-auto sm:bottom-full sm:left-0 sm:mb-1.5 sm:w-72 sm:max-h-[28rem]
                        bg-surface border border-space-30 rounded-xl shadow-lg
                        overflow-hidden animate-[scaleIn_100ms_ease-out] z-50 overflow-y-auto">
          {pendingPremium ? (
            /* Premium-model confirmation — friction before an expensive pick. */
            <div className="p-3">
              <div className="flex items-start gap-2">
                <svg className="h-4 w-4 text-hydra shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-space">
                    {pendingPremium.label} is a <span className="text-hydra">Premium</span> model
                  </p>
                  <p className="mt-1 text-[11px] leading-snug text-space-50">
                    This is among the most expensive tiers — it can cost many times more per
                    message than an Everyday model. Pick a lower tier unless this task genuinely
                    needs it.
                  </p>
                  {pendingPremium.priceText && (
                    <p className="mt-1.5 text-[11px] font-medium text-space tabular-nums">
                      Input price: {pendingPremium.priceText}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  onClick={() => setPendingPremium(null)}
                  className="px-2.5 py-1 rounded-lg text-xs font-medium bg-space-20 text-space hover:bg-space-30 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => commit(pendingPremium.id)}
                  className="px-2.5 py-1 rounded-lg text-xs font-medium bg-hydra text-white hover:opacity-90 transition-opacity"
                >
                  Use anyway
                </button>
              </div>
            </div>
          ) : (
          <>
          {/* Auto option — the cost-smart, failover-aware default. */}
          <button
            onClick={() => commit(AUTO_MODEL_ID)}
            className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 transition-colors border-b border-space-20
              ${isAuto ? "bg-jove-10 text-jove" : "hover:bg-space-20 text-space"}`}
          >
            <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-gradient-to-br from-jove to-amber-500" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium">Auto</p>
              <p className="text-[10px] text-space-40 truncate">Recommended · {autoHint}</p>
            </div>
            {isAuto && (
              <svg className="h-3.5 w-3.5 ml-auto text-jove shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>

          {/* ── Claude group — only for installs with a Claude token ── */}
          {showClaude && (
          <div>
            <div className="h-px bg-space-20" />
            <div className="px-3 pt-2 pb-1 flex items-center justify-between sticky top-0 bg-surface">
              <span className="text-[10px] uppercase tracking-wider text-space-40">Claude</span>
              {annotationFor(claudeStatus) && (
                <span className="text-[10px] text-amber-600">{annotationFor(claudeStatus)}</span>
              )}
            </div>
            {CLAUDE_MODELS.map((m) => (
              <button
                key={m.id}
                onClick={() => choose(m.id, m.label, m.cost_class)}
                className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 transition-colors
                  ${m.id === selectedModel ? "bg-jove-10 text-jove" : "hover:bg-space-20 text-space"}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                  m.id === selectedModel ? PROVIDER_DOT[m.provider] : "bg-space-40"
                }`} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{m.label}</p>
                  <p className="text-[10px] text-space-40 truncate">
                    <span className={tierColor(m.cost_class)}>{tierWord(m.cost_class)}</span>
                    {` · ${m.desc}`}
                  </p>
                </div>
                <CostMeter costClass={m.cost_class} />
                {m.id === selectedModel && (
                  <svg className="h-3.5 w-3.5 shrink-0 text-jove" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
          )}

          {/* ── OpenRouter group ── */}
          <div>
            <div className="h-px bg-space-20" />
            <div className="px-3 pt-2 pb-1 flex items-center justify-between sticky top-0 bg-surface z-10">
              <span className="text-[10px] uppercase tracking-wider text-space-40">OpenRouter</span>
              {annotationFor(orStatus) && (
                <span className="text-[10px] text-amber-600">{annotationFor(orStatus)}</span>
              )}
            </div>
            {/* Search — the catalog is hundreds of models. */}
            <div className="px-3 pb-1.5 sticky top-6 bg-surface z-10">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${orModels.length || ""} models…`}
                className="w-full px-2 py-1 text-xs rounded-md border border-space-30
                           focus:outline-none focus:ring-1 focus:ring-jove bg-space-10 text-space"
              />
            </div>
            {orModels.length === 0 ? (
              <p className="px-3 py-2 text-[10px] text-space-40">
                Catalog unavailable. Add an OpenRouter key in Settings → Connections.
              </p>
            ) : orVisible.length === 0 ? (
              <p className="px-3 py-2 text-[10px] text-space-40">No models match “{search}”.</p>
            ) : (
              orVisible.map((m) => (
                <button
                  key={m.id}
                  onClick={() => choose(m.id, m.label, m.cost_class, m.pricing)}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors
                    ${m.id === selectedModel ? "bg-jove-10 text-jove" : "hover:bg-space-20 text-space"}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    m.id === selectedModel ? PROVIDER_DOT.openrouter : "bg-space-40"
                  }`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{m.label}</p>
                    <p className="text-[10px] text-space-40 truncate">
                      {m.context_window ? `${Math.round(m.context_window / 1000)}K` : ""}
                      {m.supports_reasoning ? " · reasoning" : ""}
                      {" · "}
                      <span className={tierColor(m.cost_class)}>{tierWord(m.cost_class)}</span>
                    </p>
                  </div>
                  <CostMeter costClass={m.cost_class} />
                  {m.id === selectedModel && (
                    <svg className="h-3.5 w-3.5 shrink-0 text-jove" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))
            )}
            {orHidden > 0 && (
              <p className="px-3 py-2 text-[10px] text-space-40">
                +{orHidden} more — refine your search to narrow the list.
              </p>
            )}
          </div>
          </>
          )}
        </div>
      )}
    </div>
  );
}
